import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Download, Filter } from "lucide-react";
import { fmtMXN } from "@/lib/iva";

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function AdminReports() {
  const { user } = useAuth();
  const isStaffOnly = user?.role === "staff";
  const today = new Date();
  const monthAgo = new Date(); monthAgo.setDate(today.getDate() - 30);

  const [filters, setFilters] = useState({
    start_date: toISO(monthAgo),
    end_date: toISO(today),
    staff_id: "all",
    service_id: "all",
    status: "all",
  });
  const [staffList, setStaffList] = useState([]);
  const [services, setServices] = useState([]);
  const [data, setData] = useState({ rows: [], summary: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isStaffOnly) {
      api.get("/staff").then((r) => setStaffList(r.data || [])).catch(() => {});
    }
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run() {
    if (!filters.start_date || !filters.end_date) { toast.error("Selecciona fechas"); return; }
    if (filters.start_date > filters.end_date) { toast.error("Fecha inicio debe ser menor o igual a fin"); return; }
    setLoading(true);
    try {
      const params = { start_date: filters.start_date, end_date: filters.end_date };
      if (!isStaffOnly && filters.staff_id !== "all") params.staff_id = filters.staff_id;
      if (filters.service_id !== "all") params.service_id = filters.service_id;
      if (filters.status !== "all") params.status = filters.status;
      const { data } = await api.get("/reports/appointments", { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar el reporte");
    } finally { setLoading(false); }
  }

  function exportCSV() {
    if (!data.rows.length) { toast.error("No hay filas para exportar"); return; }
    const headers = ["Fecha", "Hora", "Cliente", "Profesional", "Servicio", "Precio", "Subtotal", "IVA", "Total", "Comisión", "Estado", "Pago"];
    const lines = [headers.join(",")];
    data.rows.forEach((r) => {
      lines.push([
        r.date, r.time,
        `"${(r.client_name || "").replace(/"/g, '""')}"`,
        `"${(r.staff_name || "").replace(/"/g, '""')}"`,
        `"${(r.service_name || "").replace(/"/g, '""')}"`,
        r.service_price.toFixed(2),
        r.subtotal.toFixed(2), r.iva.toFixed(2), r.total.toFixed(2),
        r.commission_amount.toFixed(2),
        r.status, r.payment_status,
      ].join(","));
    });
    const csv = lines.join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reporte-${filters.start_date}_${filters.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const summary = data.summary;

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Reportes</p>
          <h1 className="font-display text-4xl font-light tracking-tight mt-3">Reporte de citas</h1>
          {isStaffOnly && <p className="text-slate-500 text-sm mt-2">Vista de profesional · solo verás tus propias citas.</p>}
        </div>
        <Button variant="outline" onClick={exportCSV} disabled={!data.rows.length} data-testid="report-export-btn">
          <Download className="h-4 w-4 mr-2" /> Exportar CSV
        </Button>
      </div>

      <Card className="border-slate-200 mb-6" data-testid="report-filters">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-[#1E3A8A]" />
            <p className="overline">Filtros</p>
          </div>
          <div className={`grid gap-3 ${isStaffOnly ? "sm:grid-cols-4" : "sm:grid-cols-5"}`}>
            <div className="space-y-1.5">
              <Label>Fecha inicio *</Label>
              <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} data-testid="report-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Fecha fin *</Label>
              <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} data-testid="report-end-date" />
            </div>
            {!isStaffOnly && (
              <div className="space-y-1.5">
                <Label>Profesional</Label>
                <Select value={filters.staff_id} onValueChange={(v) => setFilters({ ...filters, staff_id: v })}>
                  <SelectTrigger data-testid="report-staff-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {staffList.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Servicio</Label>
              <Select value={filters.service_id} onValueChange={(v) => setFilters({ ...filters, service_id: v })}>
                <SelectTrigger data-testid="report-service-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {services.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Estado</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="report-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="confirmed">Confirmadas</SelectItem>
                  <SelectItem value="completed">Completadas</SelectItem>
                  <SelectItem value="cancelled">Canceladas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={run} disabled={loading} data-testid="report-run-btn">
              {loading ? "Generando…" : "Generar reporte"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="report-summary">
          <SummaryTile label="Citas" value={summary.count} />
          <SummaryTile label="Subtotal" value={fmtMXN(summary.total_subtotal)} />
          <SummaryTile label="IVA" value={fmtMXN(summary.total_iva)} />
          <SummaryTile label="Ingreso completado" value={fmtMXN(summary.total_revenue)} highlight />
          <SummaryTile label="Comisiones" value={fmtMXN(summary.total_commissions)} />
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-auto">
          <Table data-testid="report-table">
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Cliente</TableHead>
                {!isStaffOnly && <TableHead>Profesional</TableHead>}
                <TableHead>Servicio</TableHead>
                <TableHead className="text-right">Precio</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Comisión</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={isStaffOnly ? 7 : 8} className="text-center text-slate-500 py-12">
                  {loading ? "Cargando…" : "Sin resultados para este rango"}
                </TableCell></TableRow>
              )}
              {data.rows.map((r) => (
                <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">
                    <div>{r.date}</div>
                    <div className="text-xs text-slate-500">{r.time}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{r.client_name || "—"}</div>
                    <div className="text-xs text-slate-500">{r.client_email}</div>
                  </TableCell>
                  {!isStaffOnly && <TableCell className="text-sm">{r.staff_name || <span className="italic text-slate-400">Sin asignar</span>}</TableCell>}
                  <TableCell className="text-sm">{r.service_name}</TableCell>
                  <TableCell className="text-right text-sm">
                    <div>{fmtMXN(r.total)}</div>
                    {r.iva_included && <div className="text-[10px] text-slate-400">IVA incl.</div>}
                  </TableCell>
                  <TableCell className="text-right text-sm text-slate-600">{fmtMXN(r.iva)}</TableCell>
                  <TableCell className="text-right text-sm">
                    {r.commission_amount > 0 ? (
                      <>
                        <div className="font-medium" style={{ color: "#1E3A8A" }}>{fmtMXN(r.commission_amount)}</div>
                        <div className="text-[10px] text-slate-400">
                          {r.commission_mode === "percent" ? `${r.commission_value}%` : r.commission_mode === "fixed" ? "fijo" : "—"}
                        </div>
                      </>
                    ) : <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} paymentStatus={r.payment_status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryTile({ label, value, highlight = false }) {
  return (
    <div className={`rounded-lg border px-4 py-3 ${highlight ? "text-white" : "bg-white border-slate-200"}`}
      style={highlight ? { backgroundColor: "#1E3A8A", borderColor: "#1E3A8A" } : {}}>
      <p className={`text-[10px] tracking-wider uppercase ${highlight ? "text-slate-300" : "text-slate-500"}`}>{label}</p>
      <p className="font-display text-2xl font-light mt-1">{value}</p>
    </div>
  );
}

function StatusBadge({ status, paymentStatus }) {
  const map = {
    confirmed: { label: "Confirmada", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    completed: { label: "Completada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    cancelled: { label: "Cancelada", cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const s = map[status] || { label: status, cls: "bg-slate-50 text-slate-700 border-slate-200" };
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] border font-medium ${s.cls}`}>{s.label}</span>
      {paymentStatus === "paid" && <span className="text-[10px] text-emerald-700 font-medium">Pagado</span>}
    </div>
  );
}
